import type { Component } from "vue";
import {
  ChartBar,
  ChartBarStacked,
  ChartColumn,
  ChartColumnStacked,
  ChartPie,
  ChartScatter,
  Donut,
  Gauge,
  GitCompareArrows,
  Grid3x3,
  Radar,
  Table2,
  Trophy,
} from "lucide-vue-next";
import { CHART_TYPES } from "./constants";
import { BAR_CHART_TYPES, SUBTOTALS_CHART_HINT, defaultChartTypeFor, toBarChartType } from "@/composables/useChartRules";
import { isMatrixLikeCard, isMatrixMultipleLikeCard, isMultipleLikeCard, isOpenRecodeCard } from "@utils/hiddenCardQuestionType";
import type { ChartCard, ResultsCardOptions } from "@/types";

type CardMeta = Pick<ChartCard, "type" | "specs" | "questions">;

type CardOptions = Pick<ResultsCardOptions, "chart_type" | "default_display">;

interface ChartContext {
  multiPopulation?: boolean;
  hasSubtotals?: boolean;
}

interface ChartTile {
  value: string;
  label: string;
  short: string;
  icon: Component;
  disabled?: boolean;
  reason?: string;
}

export const TABLE_TILE = "table";

export const CHART_TILES: ChartTile[] = [
  { value: TABLE_TILE, label: "Table", short: "Table", icon: Table2 },
  { value: CHART_TYPES.BAR_CLUSTERED, label: "Horizontal bar", short: "H. bar", icon: ChartBar },
  { value: CHART_TYPES.COLUMN_CLUSTERED, label: "Vertical bar", short: "V. bar", icon: ChartColumn },
  { value: CHART_TYPES.BAR_STACKED_100, label: "Horizontal stacked 100%", short: "H. stacked", icon: ChartBarStacked },
  { value: CHART_TYPES.COLUMN_STACKED_100, label: "Vertical stacked 100%", short: "V. stacked", icon: ChartColumnStacked },
  { value: CHART_TYPES.PIE, label: "Pie", short: "Pie", icon: ChartPie },
  { value: CHART_TYPES.DOUGHNUT, label: "Doughnut", short: "Doughnut", icon: Donut },
  { value: CHART_TYPES.NPS, label: "NPS", short: "NPS", icon: Gauge },
  { value: CHART_TYPES.HEATMAP, label: "Heatmap", short: "Heatmap", icon: Grid3x3 },
  { value: CHART_TYPES.RADAR, label: "Radar", short: "Radar", icon: Radar },
  { value: CHART_TYPES.RANKING_FLOW, label: "Ranking flow", short: "Ranking", icon: GitCompareArrows },
  { value: CHART_TYPES.PODIUM, label: "Podium", short: "Podium", icon: Trophy },
  { value: CHART_TYPES.DOT_PLOT, label: "Dot plot", short: "Dot plot", icon: ChartScatter },
];

export const UNAVAILABLE_REASONS = {
  comparePopulations: "Needs at least two populations to compare. Add a population to this card or to the dashboard.",
  singleChoice: "Not available on multiple-choice questions and codifications: their shares do not add up to 100%.",
  nps: "Only available on NPS questions and NPS matrices.",
  matrix: "Only available on matrix questions, one row per item.",
  ranking: "Only available on ranking questions.",
  notRanking: "Ranking questions read as a ranking flow, or as bars of the answers ranked in the first positions.",
  notMatrix: "Not available on matrix questions: each item is a series of its own.",
  subtotals: SUBTOTALS_CHART_HINT,
};

const PIE_TYPES = [CHART_TYPES.PIE, CHART_TYPES.PIE_EXPLODED, CHART_TYPES.DOUGHNUT];
const STACKED_TYPES = [CHART_TYPES.BAR_STACKED_100, CHART_TYPES.COLUMN_STACKED_100];
const DISPLAY_MODES = ["chart", TABLE_TILE];

const NO_CHART_TYPES = ["q_numeric", "q_open", "q_open_multiple", "q_zipcode", "q_city", "q_email", "q_image", "q_video", "q_audio"];

export function hasChartGrid(meta: CardMeta | null | undefined): boolean {
  if (!meta?.type || NO_CHART_TYPES.includes(meta.type)) return false;
  if (isOpenRecodeCard(meta)) return false;
  return !(meta.type === "q_hidden" && meta.questions?.[0]?.question_type === "open");
}

export function isNpsCard(meta: CardMeta | null | undefined): boolean {
  return meta?.type === "q_nps" || (meta?.type === "q_matrix" && meta.specs?.matrix_type === "nps");
}

export function unavailableReason(value: string, meta: CardMeta | null | undefined, ctx: ChartContext = {}): string | null {
  if (value === TABLE_TILE) return null;
  if (ctx.hasSubtotals && !BAR_CHART_TYPES.includes(value)) return UNAVAILABLE_REASONS.subtotals;
  const matrix = isMatrixLikeCard(meta);
  const matrixMultiple = isMatrixMultipleLikeCard(meta);
  const multipleLike = isMultipleLikeCard(meta);
  if (PIE_TYPES.includes(value) || value === CHART_TYPES.PODIUM) {
    if (matrix) return UNAVAILABLE_REASONS.notMatrix;
    if (PIE_TYPES.includes(value) && multipleLike) return UNAVAILABLE_REASONS.singleChoice;
  }
  if (value === CHART_TYPES.DOT_PLOT && matrix) return UNAVAILABLE_REASONS.notMatrix;
  if (value === CHART_TYPES.DOT_PLOT && ctx.multiPopulation === false) return UNAVAILABLE_REASONS.comparePopulations;
  if (value === CHART_TYPES.RANKING_FLOW && meta?.type !== "q_ranking") return UNAVAILABLE_REASONS.ranking;
  if (STACKED_TYPES.includes(value) && meta?.type === "q_ranking") return UNAVAILABLE_REASONS.notRanking;
  if (STACKED_TYPES.includes(value) && (multipleLike || matrixMultiple)) return UNAVAILABLE_REASONS.singleChoice;
  if (value === CHART_TYPES.NPS && !isNpsCard(meta)) return UNAVAILABLE_REASONS.nps;
  if ([CHART_TYPES.HEATMAP, CHART_TYPES.RADAR].includes(value) && !matrix) return UNAVAILABLE_REASONS.matrix;
  if (value === CHART_TYPES.RADAR && matrixMultiple) return UNAVAILABLE_REASONS.singleChoice;
  return null;
}

export function chartTileOptions(meta: CardMeta | null | undefined, ctx: ChartContext = {}): ChartTile[] {
  return CHART_TILES.map((tile) => {
    const reason = unavailableReason(tile.value, meta, ctx);
    return { ...tile, disabled: !!reason, reason: reason || undefined };
  });
}

export function dashboardChartType(meta: CardMeta | null | undefined, viewOptions: CardOptions = {}): string {
  if (isNpsCard(meta) || meta?.type === "q_ranking" || isMatrixLikeCard(meta)) return defaultChartTypeFor(meta);
  return viewOptions.chart_type || defaultChartTypeFor(meta);
}

export function dashboardTile(meta: CardMeta | null | undefined, viewOptions: CardOptions = {}): string {
  if (viewOptions.default_display === TABLE_TILE) return TABLE_TILE;
  return dashboardChartType(meta, viewOptions);
}

export function displayModeFor(cardOptions: CardOptions = {}, viewOptions: CardOptions = {}): string {
  const own = cardOptions.default_display;
  if (own) return DISPLAY_MODES.includes(own) ? own : "chart";
  return viewOptions.default_display === TABLE_TILE ? TABLE_TILE : "chart";
}

export function resolveChartType(
  meta: CardMeta | null | undefined,
  cardOptions: CardOptions = {},
  viewOptions: CardOptions = {},
  ctx: ChartContext = {},
): string {
  const fit = (type: string) => (ctx.hasSubtotals ? toBarChartType(type) : type);
  const selected = fit(cardOptions.chart_type || dashboardChartType(meta, viewOptions));
  if (!unavailableReason(selected, meta, ctx)) return selected;
  const natural = fit(dashboardChartType(meta, viewOptions));
  if (!unavailableReason(natural, meta, ctx)) return natural;
  return fit(defaultChartTypeFor(meta));
}

export function resolveChartTile(
  meta: CardMeta | null | undefined,
  cardOptions: CardOptions = {},
  viewOptions: CardOptions = {},
  ctx: ChartContext = {},
): string {
  if (displayModeFor(cardOptions, viewOptions) === TABLE_TILE) return TABLE_TILE;
  return resolveChartType(meta, cardOptions, viewOptions, ctx);
}

export function chartTilePatch(
  value: string,
  meta: CardMeta | null | undefined,
  viewOptions: CardOptions = {},
): { chart_type: string | null; default_display: string | null } {
  if (value === dashboardTile(meta, viewOptions)) return { chart_type: null, default_display: null };
  if (value === TABLE_TILE) return { chart_type: null, default_display: TABLE_TILE };
  return {
    chart_type: value === dashboardChartType(meta, viewOptions) ? null : value,
    default_display: viewOptions.default_display === TABLE_TILE ? "chart" : null,
  };
}

export function extraPopulationTokens(customPopulations: string[] | null | undefined, viewTokens: string[]): string[] {
  return (customPopulations || []).filter((token) => token !== "all" && !viewTokens.includes(token));
}

export function customPopulationsWith(extraTokens: string[], viewTokens: string[]): string[] | null {
  const extras = extraPopulationTokens(extraTokens, viewTokens);
  return extras.length ? extras : null;
}
